import * as React from 'react';
import { useMediaQuery } from 'react-responsive';

import { AviaPassengerType, SeatMapRestrictions } from '@websky/graphql';
import { PassengerState } from '../../../../Checkout/store/passengers/reducers';
import { Money as MoneyType } from '../../../../types';
import { getPassengerAvatar, getPassengerLabel, TABLET_MIDDLE_WIDTH } from '../../../../utils';
import DesktopPassenger from './DesktopPassenger/DesktopPassenger';
import MobilePassenger from './MobilePassenger/MobilePassenger';
import { SelectedSeatsServiceType } from '../../../../Checkout/store/selectedServices/reducers';
import { isFreeSeat as getIsFreeSeat } from '../../SeatMap/utils';
import { useTranslation } from 'react-i18next';
import { AllowedSeatMapFilters } from '../../../types';
import { isDoubleSeatsOnSegment } from '@shared/lib/seat-map';

interface Props {
	passenger: PassengerState;
	isActive?: boolean;
	isActiveDoubleSeat?: boolean;
	seats?: Partial<SelectedSeatsServiceType>[];
	seatsPrice?: MoneyType;
	segmentsCount?: number;
	onClick?: (passengerId: string) => void;
	onClear?: (passengerId: string) => void;
	toggleExtraSeat?: (active: boolean) => void;
	recommend?: boolean;
	clearEnable?: boolean;
	isNewOrder?: boolean;
	allowedFilters?: AllowedSeatMapFilters[];
	activeFilters?: SeatMapRestrictions[];
	filteredSeatsCount?: number;
}

const Passenger: React.FC<Props> = props => {
	const { t } = useTranslation('SeatMap');

	const label = React.useMemo(() => getPassengerLabel(props.passenger), [props.passenger]);
	const avatarText = React.useMemo(() => getPassengerAvatar(props.passenger), [props.passenger]);

	const seatNumbers = props?.seats?.map(seat => seat.number) || [];
	const segmentId = props.seats?.[0]?.segmentId ?? '';
	const isDoubleSeats = isDoubleSeatsOnSegment(props.seats ?? [], segmentId);
	const isFreeSeat = props.seats?.length > 0 && props.seats.every(seat => getIsFreeSeat(seat, props.passenger.id));
	const isChangeable = isDoubleSeats
		? !props.isNewOrder && props.seats.some(seat => seat.isChangeable)
		: props?.seats?.some(seat => seat.isChangeable);
	const isTablet = useMediaQuery({ maxWidth: TABLET_MIDDLE_WIDTH });

	const disableReason =
		props.passenger.type === AviaPassengerType.INF &&
		t('Children under 2 years of age fly in the same seat with an adult');

	const onClickHandler = () => {
		if (!disableReason) {
			props.onClick(props.passenger.id);
		}
	};

	const onClearHandler = () => {
		props.onClear(props.passenger.id);
	};

	const PassengerComponent = isTablet ? MobilePassenger : DesktopPassenger;

	return (
		<PassengerComponent
			isFreeSeat={isFreeSeat}
			passengerName={label}
			passengerAvatarText={avatarText}
			passengerId={props.passenger.id}
			seatNumbers={seatNumbers}
			price={props?.seatsPrice}
			isActive={props.isActive}
			isActiveDoubleSeat={props.isActiveDoubleSeat}
			isDisabled={!!disableReason}
			recommend={props.recommend}
			changeable={isChangeable}
			onClick={onClickHandler}
			onClearSeat={onClearHandler}
			toggleExtraSeat={props.toggleExtraSeat}
			hasSeatsByPassenger={!!props.passenger.seats?.length}
			showDoubleSeatInfo={props.segmentsCount > 1}
			tooltipTitle={disableReason}
			allowedFilters={props.allowedFilters}
			activeFilters={props.activeFilters}
			filteredSeatsCount={props.filteredSeatsCount}
		/>
	);
};

export default Passenger;
